// Driver-facing self-report incident wizard. Three paths from the same
// form: my vehicle only (no third party), involved another vehicle
// (repeatable -- a driver can hit more than one car), or property damage.
// Auto-fills who's reporting and which vehicle they're in via the same
// vw_roster_shifts_enriched lookup DriverFuelCard.tsx/VehicleInspection.tsx
// use for "today's vehicle". Mandatory/optional fields come from
// incident_report_field_settings (Insurance -> Settings, admin-configurable)
// rather than being hardcoded here.
//
// Autosaves at each stage instead of only writing on final submit: picking
// a type creates the incident row as a 'draft' immediately, each "Next"
// persists that stage's fields, and photos upload the moment they're
// added. On mount it checks for an existing draft for this driver and
// offers to resume it -- so navigating away mid-report (or losing signal
// partway through, common right after a crash) never loses progress.
import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/lib/supabase";
import { formatDate } from "@/lib/dates";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Car, MapPin, Plus, Trash2, Siren, Building2, CheckCircle2, Loader2, RotateCcw } from "lucide-react";
import {
  createIncident,
  updateIncident,
  deleteIncident,
  fetchDraftIncidentForDriver,
  fetchIncidentThirdParties,
  fetchIncidentPhotos,
  saveThirdParty,
  IncidentCategory,
  InsuranceIncident,
  IncidentThirdParty,
} from "@/lib/incidentReports";
import VoiceIncidentRecorder, { VoiceIncidentFields } from "@/components/VoiceIncidentRecorder";
import GuidedVehicleDamagePhotos from "@/components/GuidedVehicleDamagePhotos";
import LiveCameraPhotoField from "@/components/LiveCameraPhotoField";

type Requirement = "mandatory" | "optional" | "hidden";
type FieldSettingsMap = Record<string, Record<string, Requirement>>;

type Step = "type" | "details" | "parties" | "photos" | "review" | "success";

interface VehicleOption {
  vehicle_number: string;
  registration: string | null;
  status: string | null;
  vehicle_type: string | null;
}

interface ThirdPartyForm {
  key: string;
  dbId?: string;
  other_driver_name: string;
  other_driver_phone: string;
  other_driver_licence: string;
  other_vehicle_rego: string;
  other_vehicle_make: string;
  other_vehicle_model: string;
  other_vehicle_damage_description: string;
  other_owner_name: string;
  other_owner_phone: string;
  other_insurer_name: string;
  other_claim_policy_number: string;
}

interface UploadedPhoto {
  id: string;
  storage_path: string;
  url: string;
}

const emptyThirdParty = (): ThirdPartyForm => ({
  key: Math.random().toString(36).slice(2),
  other_driver_name: "",
  other_driver_phone: "",
  other_driver_licence: "",
  other_vehicle_rego: "",
  other_vehicle_make: "",
  other_vehicle_model: "",
  other_vehicle_damage_description: "",
  other_owner_name: "",
  other_owner_phone: "",
  other_insurer_name: "",
  other_claim_policy_number: "",
});

const thirdPartyFromRow = (row: IncidentThirdParty): ThirdPartyForm => ({
  key: row.id,
  dbId: row.id,
  other_driver_name: row.other_driver_name || "",
  other_driver_phone: row.other_driver_phone || "",
  other_driver_licence: row.other_driver_licence || "",
  other_vehicle_rego: row.other_vehicle_rego || "",
  other_vehicle_make: row.other_vehicle_make || "",
  other_vehicle_model: row.other_vehicle_model || "",
  other_vehicle_damage_description: row.other_vehicle_damage_description || "",
  other_owner_name: row.other_owner_name || "",
  other_owner_phone: row.other_owner_phone || "",
  other_insurer_name: row.other_insurer_name || "",
  other_claim_policy_number: row.other_claim_policy_number || "",
});

const emptyDetails = () => ({
  vehicle_number: "",
  incident_date: format(new Date(), "yyyy-MM-dd"),
  incident_time: format(new Date(), "HH:mm"),
  location_description: "",
  weather_conditions: "",
  road_conditions: "",
  narrative: "",
  own_vehicle_damage_description: "",
  vehicle_location_now: "",
  police_involved: false,
  police_incident_number: "",
  witnesses_present: false,
  witness_details: "",
  injuries: false,
  injury_details: "",
  alcohol_drugs_involved: false,
});

const emptyPhotos = (): Record<string, UploadedPhoto[]> => ({ own_vehicle_damage: [], other_vehicle_damage: [], scene: [] });

const publicPhotoUrl = (path: string) => supabase.storage.from("incident-reports").getPublicUrl(path).data.publicUrl;

function getCurrentPosition(): Promise<{ lat: number; lng: number } | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });
}

const CATEGORY_META: Record<IncidentCategory, { label: string; description: string; icon: React.ElementType }> = {
  single_vehicle: { label: "My Vehicle Only", description: "No one else involved -- e.g. hit a pole, kerb, or parked object.", icon: Car },
  vehicle_collision: { label: "Involved Another Vehicle", description: "Collided with one or more other vehicles.", icon: Siren },
  property_damage: { label: "Property Damage", description: "Damaged a fence, building, or other property -- no other vehicle involved.", icon: Building2 },
  other: { label: "Other", description: "Anything else worth recording.", icon: Car },
};

function determineResumeStep(
  category: IncidentCategory,
  hasNarrative: boolean,
  partiesCount: number,
  photosByCategory: Record<string, UploadedPhoto[]>
): Step {
  if (!hasNarrative) return "details";
  if ((category === "vehicle_collision" || category === "property_damage") && partiesCount === 0) return "parties";
  const anyPhotos = Object.values(photosByCategory).some((arr) => arr.length > 0);
  if (!anyPhotos) return "photos";
  return "review";
}

const ReportIncident: React.FC<{ driverName: string }> = ({ driverName }) => {
  const [checkingDraft, setCheckingDraft] = useState(true);
  const [draftPrompt, setDraftPrompt] = useState<InsuranceIncident | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const [resuming, setResuming] = useState(false);

  const [step, setStep] = useState<Step>("type");
  const [incidentId, setIncidentId] = useState<string | null>(null);
  const [category, setCategory] = useState<IncidentCategory | null>(null);
  const [fieldSettings, setFieldSettings] = useState<FieldSettingsMap>({});
  const [vehicles, setVehicles] = useState<VehicleOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [details, setDetails] = useState(emptyDetails());
  const [thirdParties, setThirdParties] = useState<ThirdPartyForm[]>([emptyThirdParty()]);
  const [propertyForm, setPropertyForm] = useState({ property_description: "", other_owner_name: "", other_owner_phone: "" });
  const [propertyDbId, setPropertyDbId] = useState<string | null>(null);

  const [photos, setPhotos] = useState<Record<string, UploadedPhoto[]>>(emptyPhotos());
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationStatus, setLocationStatus] = useState<"pending" | "found" | "unavailable">("pending");

  // Auto-fill: today's rostered vehicle(s), same lookup DriverFuelCard.tsx uses.
  useEffect(() => {
    if (!driverName) return;
    (async () => {
      const todayStr = format(new Date(), "yyyy-MM-dd");
      const [{ data: shifts }, { data: allVehicles }] = await Promise.all([
        supabase
          .from("vw_roster_shifts_enriched")
          .select("vehicle_number")
          .eq("driver_name", driverName)
          .eq("shift_date", todayStr)
          .eq("status", "published")
          .not("vehicle_number", "is", null),
        supabase.from("vehicles").select("vehicle_number, registration, status, vehicle_type").order("vehicle_number"),
      ]);
      // No is_active flag on vehicles -- status is the equivalent (see
      // FleetManagement.tsx); missing/null status is treated as active too,
      // matching every other active-fleet filter in this codebase.
      setVehicles((allVehicles || []).filter((v) => v.status === "active" || !v.status));
      const todaysVehicles = Array.from(new Set((shifts || []).map((s) => s.vehicle_number as string)));
      if (todaysVehicles.length === 1) {
        setDetails((d) => ({ ...d, vehicle_number: todaysVehicles[0] }));
      }
    })();
  }, [driverName]);

  // Field requirement settings, loaded once for all incident categories.
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("incident_report_field_settings").select("incident_category, field_key, requirement").eq("is_active", true);
      const map: FieldSettingsMap = {};
      (data || []).forEach((row) => {
        if (!map[row.incident_category]) map[row.incident_category] = {};
        map[row.incident_category][row.field_key] = row.requirement as Requirement;
      });
      setFieldSettings(map);
    })();
  }, []);

  // Offer to resume an in-progress report for this driver, if one exists.
  useEffect(() => {
    if (!driverName) return;
    (async () => {
      const draft = await fetchDraftIncidentForDriver(driverName);
      setDraftPrompt(draft);
      setCheckingDraft(false);
    })();
  }, [driverName]);

  const requirement = (key: string): Requirement => (category && fieldSettings[category]?.[key]) || "optional";
  const isMandatory = (key: string) => requirement(key) === "mandatory";
  const isHidden = (key: string) => requirement(key) === "hidden";

  const photoCategories = useMemo(() => {
    if (category === "single_vehicle") return [{ key: "own_vehicle_damage", label: "Your Vehicle's Damage" }];
    if (category === "property_damage")
      return [
        { key: "own_vehicle_damage", label: "Your Vehicle's Damage" },
        { key: "scene", label: "The Damaged Property" },
      ];
    if (category === "vehicle_collision")
      return [
        { key: "own_vehicle_damage", label: "Your Vehicle's Damage" },
        { key: "other_vehicle_damage", label: "Other Vehicle's Damage" },
        { key: "scene", label: "The Scene" },
      ];
    return [{ key: "own_vehicle_damage", label: "Your Vehicle's Damage" }];
  }, [category]);

  const resumeDraft = async () => {
    if (!driverName || !draftPrompt) return;
    setResuming(true);
    try {
      setIncidentId(draftPrompt.id);
      setCategory(draftPrompt.incident_category);
      setDetails({
        vehicle_number: draftPrompt.vehicle_number || "",
        incident_date: draftPrompt.incident_date || format(new Date(), "yyyy-MM-dd"),
        incident_time: draftPrompt.incident_time || format(new Date(), "HH:mm"),
        location_description: draftPrompt.location_description || "",
        weather_conditions: draftPrompt.weather_conditions || "",
        road_conditions: draftPrompt.road_conditions || "",
        narrative: draftPrompt.narrative || "",
        own_vehicle_damage_description: draftPrompt.own_vehicle_damage_description || "",
        vehicle_location_now: draftPrompt.vehicle_location_now || "",
        police_involved: draftPrompt.police_involved,
        police_incident_number: draftPrompt.police_incident_number || "",
        witnesses_present: draftPrompt.witnesses_present,
        witness_details: draftPrompt.witness_details || "",
        injuries: draftPrompt.injuries,
        injury_details: draftPrompt.injury_details || "",
        alcohol_drugs_involved: draftPrompt.alcohol_drugs_involved,
      });
      if (draftPrompt.location_lat != null && draftPrompt.location_lng != null) {
        setLocation({ lat: draftPrompt.location_lat, lng: draftPrompt.location_lng });
        setLocationStatus("found");
      }

      const [parties, existingPhotos] = await Promise.all([
        fetchIncidentThirdParties(draftPrompt.id),
        fetchIncidentPhotos(draftPrompt.id),
      ]);

      if (draftPrompt.incident_category === "vehicle_collision" && parties.length > 0) {
        setThirdParties(parties.map(thirdPartyFromRow));
      } else if (draftPrompt.incident_category === "property_damage" && parties.length > 0) {
        const p = parties[0];
        setPropertyDbId(p.id);
        setPropertyForm({
          property_description: p.property_description || "",
          other_owner_name: p.other_owner_name || "",
          other_owner_phone: p.other_owner_phone || "",
        });
      }

      const photosByCategory = emptyPhotos();
      existingPhotos.forEach((ph) => {
        if (!photosByCategory[ph.category]) photosByCategory[ph.category] = [];
        photosByCategory[ph.category].push({ id: ph.id, storage_path: ph.storage_path, url: publicPhotoUrl(ph.storage_path) });
      });
      setPhotos(photosByCategory);

      setStep(determineResumeStep(draftPrompt.incident_category, !!(draftPrompt.narrative || "").trim(), parties.length, photosByCategory));
      setDraftPrompt(null);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    }
    setResuming(false);
  };

  const discardDraft = async () => {
    if (!draftPrompt) return;
    setDiscarding(true);
    try {
      await deleteIncident(draftPrompt.id);
      setDraftPrompt(null);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    }
    setDiscarding(false);
  };

  const selectCategory = async (c: IncidentCategory) => {
    setSaving(true);
    setSubmitError(null);
    try {
      if (incidentId) {
        await updateIncident(incidentId, { incident_category: c });
      } else {
        const incident = await createIncident({
          reported_by: "driver_self",
          driver_name: driverName,
          incident_category: c,
          incident_date: details.incident_date,
          incident_time: details.incident_time || null,
          vehicle_number: details.vehicle_number || null,
          status: "draft",
        });
        setIncidentId(incident.id);
      }
      setCategory(c);
      setStep("details");
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    }
    setSaving(false);
  };

  const goToPhotos = async () => {
    setStep("photos");
    if (locationStatus === "pending") {
      const pos = await getCurrentPosition();
      setLocation(pos);
      setLocationStatus(pos ? "found" : "unavailable");
      if (incidentId && pos) await updateIncident(incidentId, { location_lat: pos.lat, location_lng: pos.lng });
    }
  };

  const saveDetailsAndAdvance = async () => {
    if (!incidentId) return;
    setSaving(true);
    setSubmitError(null);
    try {
      await updateIncident(incidentId, {
        vehicle_number: details.vehicle_number,
        incident_date: details.incident_date,
        incident_time: details.incident_time || null,
        location_description: details.location_description || null,
        weather_conditions: details.weather_conditions || null,
        road_conditions: details.road_conditions || null,
        narrative: details.narrative || null,
        own_vehicle_damage_description: details.own_vehicle_damage_description || null,
        vehicle_location_now: details.vehicle_location_now || null,
        police_involved: details.police_involved,
        police_incident_number: details.police_incident_number || null,
        witnesses_present: details.witnesses_present,
        witness_details: details.witness_details || null,
        injuries: details.injuries,
        injury_details: details.injury_details || null,
        alcohol_drugs_involved: details.alcohol_drugs_involved,
      });
      if (category === "single_vehicle") await goToPhotos();
      else setStep("parties");
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    }
    setSaving(false);
  };

  // Merges onto the existing form rather than replacing it, so an earlier
  // answer this voice session already gave doesn't get clobbered by a later
  // one that didn't happen to repeat it -- a blank/false field from the AI
  // just means "wasn't mentioned this time", not "clear what was there".
  const applyVoiceFields = (fields: VoiceIncidentFields) => {
    setDetails((d) => ({
      ...d,
      vehicle_number: fields.vehicle_number || d.vehicle_number,
      incident_date: fields.incident_date || d.incident_date,
      incident_time: fields.incident_time || d.incident_time,
      location_description: fields.location_description || d.location_description,
      weather_conditions: fields.weather_conditions || d.weather_conditions,
      road_conditions: fields.road_conditions || d.road_conditions,
      narrative: fields.narrative || d.narrative,
      own_vehicle_damage_description: fields.own_vehicle_damage_description || d.own_vehicle_damage_description,
      vehicle_location_now: fields.vehicle_location_now || d.vehicle_location_now,
      police_involved: fields.police_involved || d.police_involved,
      police_incident_number: fields.police_incident_number || d.police_incident_number,
      witnesses_present: fields.witnesses_present || d.witnesses_present,
      witness_details: fields.witness_details || d.witness_details,
      injuries: fields.injuries || d.injuries,
      injury_details: fields.injury_details || d.injury_details,
      alcohol_drugs_involved: fields.alcohol_drugs_involved || d.alcohol_drugs_involved,
    }));
  };

  // Saved as each answer comes in (not just at the end) so the transcript
  // survives even if the driver abandons the voice flow partway through --
  // same "autosave at each stage" convention as the rest of this screen.
  const saveVoiceTranscript = async (transcript: string) => {
    if (!incidentId) return;
    try {
      await updateIncident(incidentId, { voice_transcript: transcript });
    } catch (e) {
      console.error("Failed to save voice transcript:", e);
    }
  };

  const addThirdParty = () => setThirdParties((prev) => [...prev, emptyThirdParty()]);
  const removeThirdParty = (key: string) => setThirdParties((prev) => (prev.length > 1 ? prev.filter((p) => p.key !== key) : prev));
  const updateThirdParty = (key: string, field: keyof ThirdPartyForm, value: string) =>
    setThirdParties((prev) => prev.map((p) => (p.key === key ? { ...p, [field]: value } : p)));

  const savePartiesAndAdvance = async () => {
    if (!incidentId) return;
    setSaving(true);
    setSubmitError(null);
    try {
      const updated = await Promise.all(
        thirdParties.map(async (p) => {
          if (!p.dbId && !p.other_driver_name.trim() && !p.other_vehicle_rego.trim() && !p.other_driver_phone.trim()) return p;
          const id = await saveThirdParty({
            id: p.dbId,
            incident_id: incidentId,
            party_type: "vehicle",
            other_driver_name: p.other_driver_name || null,
            other_driver_phone: p.other_driver_phone || null,
            other_driver_licence: p.other_driver_licence || null,
            other_vehicle_rego: p.other_vehicle_rego || null,
            other_vehicle_make: p.other_vehicle_make || null,
            other_vehicle_model: p.other_vehicle_model || null,
            other_vehicle_damage_description: p.other_vehicle_damage_description || null,
            other_owner_name: p.other_owner_name || null,
            other_owner_phone: p.other_owner_phone || null,
            other_insurer_name: p.other_insurer_name || null,
            other_claim_policy_number: p.other_claim_policy_number || null,
          });
          return { ...p, dbId: id };
        })
      );
      setThirdParties(updated);
      await goToPhotos();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    }
    setSaving(false);
  };

  const savePropertyAndAdvance = async () => {
    if (!incidentId) return;
    setSaving(true);
    setSubmitError(null);
    try {
      if (propertyForm.property_description.trim() || propertyForm.other_owner_name.trim() || propertyForm.other_owner_phone.trim()) {
        const id = await saveThirdParty({
          id: propertyDbId || undefined,
          incident_id: incidentId,
          party_type: "property",
          property_description: propertyForm.property_description || null,
          other_owner_name: propertyForm.other_owner_name || null,
          other_owner_phone: propertyForm.other_owner_phone || null,
        });
        setPropertyDbId(id);
      }
      await goToPhotos();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    }
    setSaving(false);
  };

  const detailsValid = details.vehicle_number && details.incident_date && details.narrative.trim();
  const partiesValid =
    category !== "vehicle_collision" ||
    thirdParties.every(
      (p) =>
        (!isMandatory("other_driver_name") || p.other_driver_name.trim()) &&
        (!isMandatory("other_driver_phone") || p.other_driver_phone.trim()) &&
        (!isMandatory("other_vehicle_rego") || p.other_vehicle_rego.trim())
    );
  const propertyValid = category !== "property_damage" || !isMandatory("property_description") || propertyForm.property_description.trim();
  const photosValid = photoCategories.every((pc) => isHidden(`photos_${pc.key}`) || !isMandatory(`photos_${pc.key}`) || (photos[pc.key] || []).length > 0);

  const submit = async () => {
    if (!incidentId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await updateIncident(incidentId, { status: "submitted_for_review" });
      setStep("success");
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    }
    setSubmitting(false);
  };

  const resetForm = () => {
    setStep("type");
    setIncidentId(null);
    setCategory(null);
    setDetails(emptyDetails());
    setThirdParties([emptyThirdParty()]);
    setPropertyForm({ property_description: "", other_owner_name: "", other_owner_phone: "" });
    setPropertyDbId(null);
    setPhotos(emptyPhotos());
    setLocation(null);
    setLocationStatus("pending");
    setSubmitError(null);
  };

  if (checkingDraft) {
    return (
      <Card>
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (draftPrompt) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5" />
            Unfinished Incident Report
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            You started reporting an incident on {formatDate(draftPrompt.incident_date)} and didn't finish. Pick up where you left off, or discard it and start fresh.
          </p>
          {submitError && <p className="text-sm text-red-400">{submitError}</p>}
          <div className="flex gap-2">
            <Button variant="outline" onClick={discardDraft} disabled={discarding || resuming} className="text-red-400 border-red-800">
              {discarding ? "Discarding..." : "Discard & Start New"}
            </Button>
            <Button onClick={resumeDraft} disabled={discarding || resuming} className="golden-button">
              {resuming ? "Loading..." : "Resume"}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === "success") {
    return (
      <Card>
        <CardContent className="py-12 flex flex-col items-center text-center gap-3">
          <CheckCircle2 className="w-12 h-12 text-green-500" />
          <h3 className="text-lg font-semibold">Incident Reported</h3>
          <p className="text-sm text-muted-foreground max-w-sm">
            Your report has been sent through for review. If this needs to become an insurance claim, the office
            will take it from here.
          </p>
          <Button onClick={resetForm} className="golden-button mt-2">
            Report Another Incident
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (step === "type") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Siren className="w-5 h-5" />
            Report an Incident
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {submitError && <p className="text-sm text-red-400">{submitError}</p>}
          {(["single_vehicle", "vehicle_collision", "property_damage"] as IncidentCategory[]).map((c) => {
            const meta = CATEGORY_META[c];
            const Icon = meta.icon;
            return (
              <button
                key={c}
                onClick={() => selectCategory(c)}
                disabled={saving}
                className="w-full text-left rounded-lg border border-border bg-background/40 p-4 hover:border-yellow-500/60 hover:bg-yellow-950/10 transition-colors flex items-start gap-3 disabled:opacity-60"
              >
                <Icon className="w-6 h-6 text-yellow-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">{meta.label}</p>
                  <p className="text-sm text-muted-foreground">{meta.description}</p>
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{category ? CATEGORY_META[category].label : "Report an Incident"}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {step === "details" && (
          <div className="space-y-4">
            <VoiceIncidentRecorder
              vehicleNumbers={vehicles.map((v) => v.vehicle_number)}
              vehicleAlreadyKnown={!!details.vehicle_number}
              onFieldsUpdated={applyVoiceFields}
              onTranscriptUpdated={saveVoiceTranscript}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Vehicle *</Label>
                <Select value={details.vehicle_number} onValueChange={(v) => setDetails((d) => ({ ...d, vehicle_number: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => (
                      <SelectItem key={v.vehicle_number} value={v.vehicle_number}>
                        #{v.vehicle_number} {v.registration ? `(${v.registration})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" value={details.incident_date} onChange={(e) => setDetails((d) => ({ ...d, incident_date: e.target.value }))} />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input type="time" value={details.incident_time} onChange={(e) => setDetails((d) => ({ ...d, incident_time: e.target.value }))} />
                </div>
              </div>
            </div>
            <div>
              <Label>Where did this happen?</Label>
              <Input
                value={details.location_description}
                onChange={(e) => setDetails((d) => ({ ...d, location_description: e.target.value }))}
                placeholder="e.g. Woolworths Broadway Carpark"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Weather</Label>
                <Input value={details.weather_conditions} onChange={(e) => setDetails((d) => ({ ...d, weather_conditions: e.target.value }))} placeholder="e.g. Raining" />
              </div>
              <div>
                <Label>Road Conditions</Label>
                <Input value={details.road_conditions} onChange={(e) => setDetails((d) => ({ ...d, road_conditions: e.target.value }))} placeholder="e.g. Wet, Sealed" />
              </div>
            </div>
            <div>
              <Label>What happened? *</Label>
              <Textarea value={details.narrative} onChange={(e) => setDetails((d) => ({ ...d, narrative: e.target.value }))} rows={3} />
            </div>
            <div>
              <Label>Damage to your vehicle</Label>
              <Textarea
                value={details.own_vehicle_damage_description}
                onChange={(e) => setDetails((d) => ({ ...d, own_vehicle_damage_description: e.target.value }))}
                rows={2}
              />
            </div>
            <div>
              <Label>Vehicle's Current Location</Label>
              <Input
                value={details.vehicle_location_now}
                onChange={(e) => setDetails((d) => ({ ...d, vehicle_location_now: e.target.value }))}
                placeholder="e.g. Still driveable, towed, parked at scene"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox checked={details.alcohol_drugs_involved} onCheckedChange={(v) => setDetails((d) => ({ ...d, alcohol_drugs_involved: !!v }))} />
                <Label className="text-sm">Alcohol or drugs involved</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={details.police_involved} onCheckedChange={(v) => setDetails((d) => ({ ...d, police_involved: !!v }))} />
                <Label className="text-sm">Police involved</Label>
              </div>
              {details.police_involved && (
                <Input
                  value={details.police_incident_number}
                  onChange={(e) => setDetails((d) => ({ ...d, police_incident_number: e.target.value }))}
                  placeholder="Police incident number (if given)"
                  className="ml-6"
                />
              )}
              <div className="flex items-center gap-2">
                <Checkbox checked={details.witnesses_present} onCheckedChange={(v) => setDetails((d) => ({ ...d, witnesses_present: !!v }))} />
                <Label className="text-sm">Witnesses</Label>
              </div>
              {details.witnesses_present && (
                <Input
                  value={details.witness_details}
                  onChange={(e) => setDetails((d) => ({ ...d, witness_details: e.target.value }))}
                  placeholder="Witness name / contact if you got it"
                  className="ml-6"
                />
              )}
              <div className="flex items-center gap-2">
                <Checkbox checked={details.injuries} onCheckedChange={(v) => setDetails((d) => ({ ...d, injuries: !!v }))} />
                <Label className="text-sm">Anyone injured</Label>
              </div>
              {details.injuries && (
                <Textarea
                  value={details.injury_details}
                  onChange={(e) => setDetails((d) => ({ ...d, injury_details: e.target.value }))}
                  placeholder="Who, and what kind of injury"
                  className="ml-6"
                  rows={2}
                />
              )}
            </div>
            {submitError && <p className="text-sm text-red-400">{submitError}</p>}
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep("type")}>
                Back
              </Button>
              <Button className="golden-button" disabled={!detailsValid || saving} onClick={saveDetailsAndAdvance}>
                {saving ? "Saving..." : "Next"}
              </Button>
            </div>
          </div>
        )}

        {step === "parties" && category === "vehicle_collision" && (
          <div className="space-y-4">
            {thirdParties.map((p, idx) => (
              <div key={p.key} className="rounded-lg border border-border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-yellow-500">Other Vehicle #{idx + 1}</p>
                  {thirdParties.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeThirdParty(p.key)} className="text-red-400 h-7 px-2">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Their Name {isMandatory("other_driver_name") && "*"}</Label>
                    <Input value={p.other_driver_name} onChange={(e) => updateThirdParty(p.key, "other_driver_name", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Their Phone {isMandatory("other_driver_phone") && "*"}</Label>
                    <Input value={p.other_driver_phone} onChange={(e) => updateThirdParty(p.key, "other_driver_phone", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Vehicle Plate {isMandatory("other_vehicle_rego") && "*"}</Label>
                    <Input value={p.other_vehicle_rego} onChange={(e) => updateThirdParty(p.key, "other_vehicle_rego", e.target.value)} />
                  </div>
                  {!isHidden("other_driver_licence") && (
                    <div>
                      <Label className="text-xs">Their Licence Number</Label>
                      <Input value={p.other_driver_licence} onChange={(e) => updateThirdParty(p.key, "other_driver_licence", e.target.value)} />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Vehicle Make</Label>
                    <Input value={p.other_vehicle_make} onChange={(e) => updateThirdParty(p.key, "other_vehicle_make", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Vehicle Model</Label>
                    <Input value={p.other_vehicle_model} onChange={(e) => updateThirdParty(p.key, "other_vehicle_model", e.target.value)} />
                  </div>
                  {!isHidden("other_insurer_name") && (
                    <div>
                      <Label className="text-xs">Their Insurer (if known)</Label>
                      <Input value={p.other_insurer_name} onChange={(e) => updateThirdParty(p.key, "other_insurer_name", e.target.value)} />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Their Claim/Policy Number (if known)</Label>
                    <Input value={p.other_claim_policy_number} onChange={(e) => updateThirdParty(p.key, "other_claim_policy_number", e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Damage to their vehicle</Label>
                  <Textarea rows={2} value={p.other_vehicle_damage_description} onChange={(e) => updateThirdParty(p.key, "other_vehicle_damage_description", e.target.value)} />
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addThirdParty}>
              <Plus className="w-4 h-4 mr-1" />
              Add Another Vehicle
            </Button>
            {submitError && <p className="text-sm text-red-400">{submitError}</p>}
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep("details")}>
                Back
              </Button>
              <Button className="golden-button" disabled={!partiesValid || saving} onClick={savePartiesAndAdvance}>
                {saving ? "Saving..." : "Next"}
              </Button>
            </div>
          </div>
        )}

        {step === "parties" && category === "property_damage" && (
          <div className="space-y-3">
            <div>
              <Label>What was damaged? {isMandatory("property_description") && "*"}</Label>
              <Textarea
                rows={2}
                value={propertyForm.property_description}
                onChange={(e) => setPropertyForm((p) => ({ ...p, property_description: e.target.value }))}
                placeholder="e.g. Wooden fence at 12 Smith St"
              />
            </div>
            {!isHidden("other_owner_name") && (
              <div>
                <Label>Owner's Name (if known)</Label>
                <Input value={propertyForm.other_owner_name} onChange={(e) => setPropertyForm((p) => ({ ...p, other_owner_name: e.target.value }))} />
              </div>
            )}
            {!isHidden("other_owner_phone") && (
              <div>
                <Label>Owner's Phone (if known)</Label>
                <Input value={propertyForm.other_owner_phone} onChange={(e) => setPropertyForm((p) => ({ ...p, other_owner_phone: e.target.value }))} />
              </div>
            )}
            {submitError && <p className="text-sm text-red-400">{submitError}</p>}
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep("details")}>
                Back
              </Button>
              <Button className="golden-button" disabled={!propertyValid || saving} onClick={savePropertyAndAdvance}>
                {saving ? "Saving..." : "Next"}
              </Button>
            </div>
          </div>
        )}

        {step === "photos" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4" />
              {locationStatus === "pending" && "Getting your location..."}
              {locationStatus === "found" && "Location captured -- attached to your photos."}
              {locationStatus === "unavailable" && "Location unavailable -- continuing without it."}
            </div>
            {photoCategories.map((pc) =>
              pc.key === "own_vehicle_damage" && incidentId ? (
                <div key={pc.key} className="space-y-2">
                  <Label>
                    {pc.label} {isMandatory(`photos_${pc.key}`) && "*"}
                  </Label>
                  <GuidedVehicleDamagePhotos
                    incidentId={incidentId}
                    vehicleType={vehicles.find((v) => v.vehicle_number === details.vehicle_number)?.vehicle_type ?? null}
                    existingPhotos={photos[pc.key] || []}
                    location={location}
                    uploadedBy={driverName}
                    onPhotosChanged={(next) => setPhotos((prev) => ({ ...prev, [pc.key]: next }))}
                  />
                </div>
              ) : incidentId ? (
                <div key={pc.key} className="space-y-2">
                  <Label>
                    {pc.label} {isMandatory(`photos_${pc.key}`) && "*"}
                  </Label>
                  <LiveCameraPhotoField
                    incidentId={incidentId}
                    category={pc.key}
                    photos={photos[pc.key] || []}
                    location={location}
                    uploadedBy={driverName}
                    onPhotosChanged={(next) => setPhotos((prev) => ({ ...prev, [pc.key]: next }))}
                  />
                </div>
              ) : null
            )}
            {submitError && <p className="text-sm text-red-400">{submitError}</p>}
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(category === "single_vehicle" ? "details" : "parties")}>
                Back
              </Button>
              <Button className="golden-button" disabled={!photosValid} onClick={() => setStep("review")}>
                Next
              </Button>
            </div>
          </div>
        )}

        {step === "review" && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Review before submitting -- once sent, this goes to the office for review.
            </p>
            <div className="rounded-lg border border-border p-3 text-sm space-y-1">
              <p>
                <span className="text-muted-foreground">Type:</span> {category && CATEGORY_META[category].label}
              </p>
              <p>
                <span className="text-muted-foreground">Vehicle:</span> #{details.vehicle_number}
              </p>
              <p>
                <span className="text-muted-foreground">Date:</span> {formatDate(details.incident_date)} {details.incident_time}
              </p>
              <p>
                <span className="text-muted-foreground">What happened:</span> {details.narrative}
              </p>
              {category === "vehicle_collision" && <p className="text-muted-foreground">{thirdParties.length} other vehicle(s) recorded.</p>}
              <p className="text-muted-foreground">
                {Object.values(photos).reduce((sum, arr) => sum + arr.length, 0)} photo(s) attached.
              </p>
            </div>
            {submitError && <p className="text-sm text-red-400">{submitError}</p>}
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep("photos")}>
                Back
              </Button>
              <Button className="golden-button" disabled={submitting} onClick={submit}>
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Report"
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReportIncident;
